import React, { useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CrossTabCell, CrossTabResult } from "./pivotUtils";
import { formatAbbreviatedMoney } from "../Money";
import { Tooltip } from "primereact/tooltip";
import styles from "./PivotTable.module.css";

type CrossTabTableProps = {
  data: CrossTabResult;
  rowLabel: string;
  colLabel: string;
};

const MAX_COLUMNS = 15;

type FlatRow = {
  _rowKey: string;
  _total: CrossTabCell;
  [colKey: string]: CrossTabCell | string;
};

const CELL_TOOLTIP_CLASS = "ct-cell-tip";

const cellBody = (colKey: string) => {
  return (rowData: FlatRow) => {
    const cell = rowData[colKey] as CrossTabCell | undefined;
    if (!cell || cell.tiv === 0) return null;
    return (
      <span
        className={CELL_TOOLTIP_CLASS}
        data-pr-tooltip={`${cell.count.toLocaleString()} locations`}
      >
        {formatAbbreviatedMoney(cell.tiv)}
      </span>
    );
  };
};

const totalBody = (rowData: FlatRow) => {
  const cell = rowData._total as CrossTabCell;
  if (!cell || cell.tiv === 0) return null;
  return <b>{formatAbbreviatedMoney(cell.tiv)}</b>;
};

export const CrossTabTable: React.FC<CrossTabTableProps> = ({
  data,
  rowLabel,
  colLabel,
}) => {
  const visibleCols = useMemo(
    () =>
      data.columnKeys.length > MAX_COLUMNS
        ? data.columnKeys.slice(0, MAX_COLUMNS)
        : data.columnKeys,
    [data.columnKeys]
  );
  const truncated = data.columnKeys.length > MAX_COLUMNS;

  const cellBodies = useMemo(
    () => new Map(visibleCols.map((ck) => [ck, cellBody(ck)])),
    [visibleCols]
  );

  const flatRows: FlatRow[] = useMemo(
    () =>
      data.rows.map((row) => {
        const flat: FlatRow = {
          _rowKey: row.rowKey,
          _total: row.total,
        };
        for (const ck of visibleCols) {
          flat[ck] = row.cells[ck] ?? { count: 0, tiv: 0 };
        }
        return flat;
      }),
    [data, visibleCols]
  );

  const colFooter = (colKey: string) => {
    const ct = data.columnTotals.get(colKey);
    return ct ? formatAbbreviatedMoney(ct.tiv) : "";
  };

  return (
    <div className={styles.crossTabWrap}>
      <Tooltip target={`.${CELL_TOOLTIP_CLASS}`} position="top" />
      {truncated && (
        <div className={styles.crossTabTruncation}>
          Showing top {MAX_COLUMNS} of {data.columnKeys.length} {colLabel}{" "}
          values by TIV.
        </div>
      )}
      <DataTable
        value={flatRows}
        stripedRows
        scrollable
        scrollHeight="500px"
        dataKey="_rowKey"
        size="small"
        tableStyle={{ minWidth: "100%" }}
      >
        <Column
          field="_rowKey"
          header={rowLabel}
          frozen
          style={{ width: "120px", fontWeight: "bold" }}
          footer="Total"
        />
        {visibleCols.map((ck) => (
          <Column
            key={ck}
            header={ck}
            body={cellBodies.get(ck)}
            align="right"
            alignHeader="right"
            footerStyle={{ textAlign: "right" }}
            footer={colFooter(ck)}
          />
        ))}
        <Column
          header="Total"
          body={totalBody}
          align="right"
          alignHeader="right"
          style={{ fontWeight: "bold" }}
          footerStyle={{ textAlign: "right" }}
          footer={formatAbbreviatedMoney(data.grandTotal.tiv)}
        />
      </DataTable>
    </div>
  );
};
